import React, { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { API, api, mediaUrl } from "../lib/api";
import { Navbar } from "../components/Navbar";
import { useCart } from "../lib/cart";
import { CheckCircle2, Download, Loader2, XCircle } from "lucide-react";

export default function Success() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const { clear } = useCart();
  const [state, setState] = useState("checking"); // checking | paid | failed | timeout
  const [data, setData] = useState(null);
  const attempts = useRef(0);
  const cleared = useRef(false);

  useEffect(() => {
    if (!sessionId) { setState("failed"); return; }
    let active = true;
    const poll = async () => {
      if (attempts.current >= 8) { if (active) setState("timeout"); return; }
      attempts.current += 1;
      try {
        const { data } = await api.get(`/checkout/status/${sessionId}`);
        if (!active) return;
        if (data.payment_status === "paid") {
          setData(data); setState("paid");
          if (!cleared.current) { clear(); cleared.current = true; }
          return;
        }
        if (data.status === "expired") { setState("failed"); return; }
        setTimeout(poll, 2000);
      } catch {
        if (active) setTimeout(poll, 2000);
      }
    };
    poll();
    return () => { active = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId]);

  return (
    <div className="App relative">
      <div className="vm-grain" />
      <Navbar />
      <div className="max-w-2xl mx-auto px-5 sm:px-8 py-20 relative z-10 text-center">
        {state === "checking" && (
          <div className="vm-fade-up" data-testid="success-checking">
            <Loader2 className="w-12 h-12 mx-auto animate-spin text-zinc-400" />
            <h1 className="font-head text-2xl mt-6">Confirming your payment…</h1>
            <p className="text-zinc-500 mt-2 text-sm">Hang tight, this only takes a moment.</p>
          </div>
        )}

        {state === "paid" && (
          <div className="vm-fade-up" data-testid="success-paid">
            <CheckCircle2 className="w-14 h-14 mx-auto text-emerald-400" />
            <h1 className="font-head text-3xl sm:text-4xl tracking-tight font-light mt-6">Payment successful</h1>
            <p className="text-zinc-400 mt-3">A receipt was sent to <span className="text-white">{data.email}</span>. Download your files below.</p>
            <div className="mt-10 space-y-3 text-left" data-testid="download-list">
              {data.downloads.map((d) => (
                <a
                  key={d.product_id}
                  href={`${API.replace(/\/api$/, "")}${d.url}`}
                  data-testid={`download-${d.product_id}`}
                  className="vm-glass rounded-xl p-4 flex items-center gap-4 hover:border-white/25 transition-colors group"
                >
                  <img src={mediaUrl(d.cover_path)} alt="" className="w-16 h-12 object-cover rounded-md border border-white/10" />
                  <div className="flex-1 min-w-0">
                    <p className="font-head font-medium truncate">{d.title}</p>
                    <p className="text-xs text-zinc-500 truncate">{d.file_name}</p>
                  </div>
                  <span className="inline-flex items-center gap-2 bg-white text-black text-sm px-4 py-2 rounded-md group-hover:bg-zinc-200 transition-colors">
                    <Download className="w-4 h-4" /> Download
                  </span>
                </a>
              ))}
            </div>
            <Link to="/" className="inline-block mt-10 text-sm text-zinc-400 hover:text-white transition-colors">← Continue shopping</Link>
          </div>
        )}

        {(state === "failed" || state === "timeout") && (
          <div className="vm-fade-up" data-testid="success-failed">
            <XCircle className="w-14 h-14 mx-auto text-red-400" />
            <h1 className="font-head text-2xl mt-6">{state === "timeout" ? "Still processing" : "Payment not completed"}</h1>
            <p className="text-zinc-400 mt-3">
              {state === "timeout"
                ? "We couldn't confirm the payment yet. Check your email shortly or refresh this page."
                : "Your payment was cancelled or failed. No charge was made."}
            </p>
            <Link to="/cart" className="inline-block mt-8 bg-white text-black px-6 py-3 rounded-md font-medium hover:bg-zinc-200 transition-colors">Back to cart</Link>
          </div>
        )}
      </div>
    </div>
  );
}
